import React, { useState } from 'react';
import { Lock, Unlock, Building2, FileText, Clock, ChevronDown, ChevronRight, AlertCircle, XCircle, Link, Eye } from 'lucide-react';

/** Purpose: Executes { permission, records, on revoke, on generate link, on view pdf, is expired = false }. Inputs: { permission, records, onRevoke, onGenerateLink, onViewPdf, isExpired = false }. Output: Operation result. */
export default function PermissionCard({ permission, records, onRevoke, onGenerateLink, onViewPdf, isExpired = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const recordIds = permission.recordIds || [];
  const fallbackRecords = permission.records || [];
  const associatedRecords = recordIds.length > 0
    ? recordIds
        .map(id => records.find(r => r.id === id) || fallbackRecords.find(r => r.id === id))
        .filter(Boolean)
    : fallbackRecords;

  /** Purpose: Executes parse local date. Inputs: value. Output: Operation result. */
  const parseLocalDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const str = String(value);
    if (str.includes('T')) return new Date(str);
    return new Date(`${str}T00:00:00`);
  };

  /** Purpose: Executes format date. Inputs: value. Output: Operation result. */
  const formatDate = (value) => {
    const date = parseLocalDate(value);
    return date ? date.toLocaleDateString() : 'N/A';
  };

  /** Purpose: Executes compute days until expiry. Inputs: none. Output: Operation result. */
  const computeDaysUntilExpiry = () => {
    const expiry = parseLocalDate(permission.expiryDate);
    if (!expiry) return null;
    return Math.ceil((expiry.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysUntilExpiry = permission.daysUntilExpiry ?? computeDaysUntilExpiry();
  const isExpiringSoon = permission.isExpiringSoon ?? (daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0);

  /** Purpose: Executes access label. Inputs: (. Output: Operation result. */
  const accessLabel = (() => {
    return 'Read Only';
  })();

  const recordCount = recordIds.length || fallbackRecords.length;

  return (
    <div className={`bg-white rounded-2xl shadow-card border border-slate-200/60 card-lift overflow-hidden ${
      isExpired ? 'opacity-60' : ''
    }`}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <div className={`p-3 rounded-xl ${
              isExpired ? 'bg-slate-100' : 'bg-gradient-to-br from-emerald-500 to-green-600'
            }`}>
              {isExpired ? (
                <Lock className="w-5 h-5 text-slate-500" />
              ) : (
                <Unlock className="w-5 h-5 text-white" />
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                <h4 className="text-base font-semibold text-slate-900 tracking-tight">{permission.grantedTo}</h4>
                <span className={`px-2.5 py-0.5 text-[11px] font-semibold rounded-full ${
                  permission.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700'
                    : permission.status === 'expired'
                    ? 'bg-slate-100 text-slate-500'
                    : 'bg-red-50 text-red-600'
                }`}>
                  {permission.status}
                </span>
                {isExpiringSoon && (
                  <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {daysUntilExpiry ? `${daysUntilExpiry}d left` : 'Expiring soon'}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  {permission.organization}
                </span>
                <span className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  {recordCount} record{recordCount !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  {formatDate(permission.grantedDate)} - {formatDate(permission.expiryDate)}
                </span>
              </div>

              <div className="mt-2.5 text-sm">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Purpose: </span>
                <span className="text-slate-500">{permission.purpose}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-slate-100' : 'hover:bg-slate-100'}`}>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-5 space-y-4 animate-fade-in">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Access Details</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-xs text-slate-400 mb-0.5">Access Level</p>
                <p className="font-semibold text-slate-900">{accessLabel}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-xs text-slate-400 mb-0.5">Times Accessed</p>
                <p className="font-semibold text-slate-900">{permission.accessCount}</p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Shared Records</p>
            <div className="space-y-1.5">
              {associatedRecords.map(record => (
                <div key={record.id} className="flex items-center gap-2.5 text-sm text-slate-600 bg-white px-3.5 py-2.5 rounded-xl border border-slate-100">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-medium text-slate-700">{record.title}</span>
                  <span className="text-slate-300">&middot;</span>
                  <span className="text-xs text-slate-400">
                    {record.type || record.recordType || 'Record'}{record.date || record.recordDate ? ` \u00b7 ${formatDate(record.date || record.recordDate)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {!isExpired && typeof onGenerateLink === 'function' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateLink(permission);
                }}
                className="px-4 py-2 bg-gradient-to-r from-brand-600 to-violet-600 text-white rounded-xl text-sm font-medium hover:shadow-glow-blue transition-all flex items-center gap-2"
              >
                <Link className="w-4 h-4" />
                Share Link
              </button>
            )}
            {typeof onViewPdf === 'function' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewPdf(permission);
                }}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                View
              </button>
            )}
            {!isExpired && typeof onRevoke === 'function' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRevoke(permission.id);
                }}
                className="px-4 py-2 bg-red-50 border border-red-200/60 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 transition-all flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Revoke
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
